import clsx from "clsx";
import { useTransitionState } from "react-transition-state";
import { useEffect } from "react";

export default function ProjectsBackdrop({
  className,
  scale,
}: {
  className?: string;
  scale: number;
}) {
  const isActive = scale === 1;
  const [{ status, isMounted }, toggle] = useTransitionState({
    timeout: {
      enter: 700, // Matches overlay enter duration
      exit: 900, // Matches orb exit (200ms) + overlay exit (700ms)
    },
    mountOnEnter: true,
    unmountOnExit: true,
  });

  useEffect(() => {
    toggle(isActive);
  }, [isActive, toggle]);

  const overlayAnimClasses = clsx(
    "transition-opacity duration-200 ease-out",
    {
      "opacity-100": status === "entered",
      "!delay-400 opacity-0": status === "exiting" || status === "exited", // Wait for orbs to exit
      "opacity-0 !delay-0": status === "preEnter",
    },
    className
  );

  const orbWrapperAnimClasses = clsx(
    "fixed w-full h-screen transition-all duration-200 ease-out opacity-0 backdrop-opacity-0 delay-[400ms]",
    {
      "opacity-100 backdrop-opacity-100 !delay-[200ms]":
        status === "entering" || status === "entered", // Wait for overlay enter
      "opacity-0 !delay-0 backdrop-opacity-0": status === "exiting" || status === "preEnter",
    }
  );

  if (!isMounted) return null;

  return (
    <div className="fixed w-full h-screen">
      {/* Base dark gradient */}
      <div className="sticky inset-0 pointer-events-none z-0">
        <div className={orbWrapperAnimClasses}>
          {/* Darker base gradient with more depth */}
          <div className="absolute w-full h-full bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950" />
          
          {/* Additional depth layer */}
          <div className="absolute w-full h-full bg-gradient-to-tr from-purple-950/80 via-transparent to-indigo-950/80" />
          
          {/* Radial gradient for depth */}
          <div className="absolute inset-0 bg-radial-gradient from-purple-900/30 via-transparent to-transparent" 
               style={{ backgroundImage: 'radial-gradient(ellipse at top left, rgba(88, 28, 135, 0.3), transparent 50%)' }} />
          <div className="absolute inset-0 bg-radial-gradient from-indigo-900/30 via-transparent to-transparent" 
               style={{ backgroundImage: 'radial-gradient(ellipse at bottom right, rgba(30, 58, 138, 0.3), transparent 50%)' }} />

          {/* Dynamic colorful orbs with better positioning */}
          <div className="absolute w-[600px] h-[600px] bg-pink-500/40 rounded-full -top-32 -left-32 mix-blend-screen blur-3xl animate-pulse" 
               style={{ animationDuration: '8s', animationTimingFunction: 'ease-in-out' }} />
          <div className="absolute w-[500px] h-[500px] bg-purple-500/50 rounded-full -bottom-20 right-32 mix-blend-screen blur-3xl" />
          <div className="absolute w-[400px] h-[400px] bg-blue-500/40 rounded-full top-1/3 left-1/4 transform -translate-x-1/2 -translate-y-1/2 mix-blend-screen blur-3xl" />
          <div className="absolute w-[450px] h-[450px] bg-violet-500/35 rounded-full bottom-1/4 left-1/3 mix-blend-screen blur-3xl" />
          <div className="absolute w-[350px] h-[350px] bg-fuchsia-500/40 rounded-full top-1/2 right-1/4 transform translate-y-1/2 mix-blend-screen blur-3xl" />
          
          {/* Subtle animated shimmer */}
          <div className="absolute inset-0 overflow-hidden"
               style={{ 
                 background: 'linear-gradient(90deg, transparent, rgba(147, 51, 234, 0.15), transparent)',
                 backgroundSize: '200% 100%',
                 animation: 'shimmer 12s ease-in-out infinite'
               }} />
        </div>
      </div>

      {/* Darker staggered overlay elements */}
      <div className={overlayAnimClasses}>
        <div className="absolute inset-0 z-20 !backdrop-blur-3xl bg-black/40 transition-opacity duration-500" />
        <div className="absolute inset-0 z-20 !backdrop-blur-3xl bg-gradient-to-b from-black/30 via-transparent to-black/40 transition-opacity duration-500 delay-100" />
        <div className="absolute inset-0 z-20 !backdrop-blur-3xl bg-gradient-to-t from-black/25 via-transparent to-black/30 transition-opacity duration-500 delay-200" />
      </div>
    </div>
  );
}
